"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Calendar, CircleCheck as CheckCircle2, Search, UserCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const relatedServices = [{
  title: "B2B SEO & Lead Generation",
  to: "/B2BSeoLeadGenerationPage",
  badge: "Commercial Search",
  description: "Target decision-makers actively researching your solutions. We build sustainable organic pipelines for high-value contracts.",
  highlights: ["High-intent commercial keywords", "Account-focused content mapping", "Direct technical consultation"],
  icon: Search
}, {
  title: "Freelance SEO Consultant",
  to: "/FreelanceSeoConsultantPage",
  badge: "Direct Partnership",
  description: "Partner with an independent senior practitioner. You get strategy, audits, and hands-on execution directly from me.",
  highlights: ["Zero account management friction", "Tailored roadmap for your targets", "Transparent reporting and guidance"],
  icon: UserCheck
}];
export default function RelatedServices() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 18
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="relatedservicescta" data-nav="light" className="bg-background py-20 md:py-28 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div className="max-w-2xl mx-auto text-center mb-14 md:mb-18">
          <Badge variant="secondary" className="mb-3 px-3 py-1 font-medium text-xs tracking-wide">
            Direct Senior Guidance
          </Badge>
          <h2 className="font-default text-3xl sm:text-4xl font-semibold tracking-tight text-foreground mb-4">
            Explore related SEO services
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Discover complementary search services that align with your growth
            goals, from commercial lead generation to direct freelance consultancy.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-16">
          {relatedServices.map((service, index) => {
          const IconComponent = service.icon;
          return <motion.div key={service.title} data-index={index} variants={itemVariants} className="h-full">
                <Card className="h-full border border-border bg-card shadow-xs hover:shadow-md transition-[border-color,box-shadow] duration-200 flex flex-col justify-between rounded-xl">
                  <CardContent className="p-7 sm:p-8 flex flex-col h-full justify-between">
                    <div>
                      <div className="flex items-center justify-between gap-4 mb-5">
                        <div className="size-11 rounded-lg bg-secondary flex items-center justify-center text-primary">
                          <IconComponent className="size-5" />
                        </div>
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {service.badge}
                        </span>
                      </div>

                      <h3 className="font-default text-xl sm:text-2xl font-semibold text-card-foreground mb-3">
                        {service.title}
                      </h3>

                      <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-6">
                        {service.description}
                      </p>

                      <ul className="space-y-2.5 mb-8 border-t border-border/60 pt-5">
                        {service.highlights.map((highlight, hIndex) => <li key={hIndex} data-index={hIndex} className="flex items-center gap-2.5 text-xs sm:text-sm text-foreground/85">
                            <CheckCircle2 className="size-4 text-primary shrink-0" />
                            <span>{highlight}</span>
                          </li>)}
                      </ul>
                    </div>

                    <div className="pt-2">
                      <Button asChild variant="outline" className="w-full justify-between group hover:border-primary/50">
                        <Link to={service.to}>
                          <span>View {service.title}</span>
                          <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Final Consultation CTA */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="rounded-2xl border border-border bg-gradient-to-b from-card via-card to-secondary/30 p-8 sm:p-12 text-center shadow-xs">
          <div className="max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-medium mb-4">
              <SafeHydrate><Calendar className="size-3.5 text-primary" /></SafeHydrate>
              <span>Free 30-Minute SEO Strategy Call</span>
            </div>

            <h3 className="font-default text-2xl sm:text-3xl font-semibold text-foreground tracking-tight mb-3">
              Ready to grow your organic visibility?
            </h3>

            <p className="text-muted-foreground text-base sm:text-lg mb-8 max-w-xl mx-auto leading-relaxed">
              Speak with me directly to audit your current search position, find
              clear wins, and plan a sustainable roadmap for your business.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="w-full sm:w-auto px-8 py-6 text-base font-medium shadow-sm hover:shadow">
                <Link to="/ContactPage">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto px-7 py-6 text-base">
                <Link to="/CaseStudiesPage">
                  <span>Explore Genuine Case Studies</span>
                </Link>
              </Button>
            </div>

            <p className="text-xs text-muted-foreground mt-4">
              Explore relevant services &bull; Honest recommendations &bull; No obligation
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}